<template>
  <cg-select
    v-model="modelValue"
    :items="filteredItems"
    :loading="loading"
    filterable
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template #selection="{ text, value }">
      {{ showSelection(text, value) }}
    </template>

    <template v-for="(_, slotName) in $slots">
      <template :slot="slotName">
        <slot :name="slotName" />
      </template>
    </template>
  </cg-select>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import type { AxiosInstance } from 'axios';
import CgSelect from '../CgSelect';

import createAxiosInstance from '../../assets/axios/createAxiosInstance';

type CgSelectInstance = InstanceType<typeof CgSelect>

interface ICountryItem {
  text: string;
  value: string;
}

export default Vue.extend({
  name: 'CgCountrySelect',
  components: {
    CgSelect,
  },
  props: {
    /**
     * The input’s value
     * @model
     */
    value: {
      type: String,
      required: false,
      default: null
    },
    /**
     * Api base URL - at default it'll try to take URL from .env file
     * **process.env.VUE_APP_API_URL** || **process.env.mainApiBaseUrl**
     */
    apiBaseUrl: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Shows the country code as the selected value
     */
    showCountryCodeForSelected: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * An array of allowed country codes, if empty it'll show all countries
     */
    allowedCountries: {
      type: Array as PropType<Array<string>>,
      required: false,
      default: () => ([]),
    },
  },
  data: () => ({
    items: [] as Array<ICountryItem>,
    loading: false,
    apiInstance: null as AxiosInstance | null,
  }),
  computed: {
    modelValue: {
      get (): string | null { return this.value },
      set (val: string) { this.$emit('input', val) }
    },
    filteredItems (): Array<ICountryItem> {
      const { items, allowedCountries } = this

      if (allowedCountries.length) {
        return items?.filter(({ value }) => allowedCountries?.includes(value)) ?? []
      }

      return items ?? []
    }
  },
  created () {
    const { apiBaseUrl } = this
    
    this.apiInstance = createAxiosInstance(apiBaseUrl || undefined);
  },
  mounted () {
    const countriesList = localStorage.getItem('countriesList')
    this.items = (countriesList ? JSON.parse(countriesList) : []);

    if (!this.items.length) { this.loadCountriesList() }
  },
  methods: {
    showSelection (text: string, value: string): string {
      const { showCountryCodeForSelected } = this

      return showCountryCodeForSelected ? value : text;
    },
    loadCountriesList (): void {
      this.loading = true;

      this.apiInstance?.get<Array<ICountryItem>>('/api/shared/data/countries')
        .then(({ data }) => {
          this.items = data || [];
          localStorage.setItem('countriesList', JSON.stringify(data || []));
        })
        .finally(() => (this.loading = false));
    },
    getCgSelectInstance (): CgSelectInstance {
      return this.$children[0] as CgSelectInstance
    },
    /**
     * Validates input. Returns **true** if successful and **false** if not.
     * @public
     */
    validate (): boolean {
      const cgSelectInstance = this.getCgSelectInstance()
      return cgSelectInstance.validate()
    },
    /**
     * Resets validation.
     * @public
     */
    resetValidation (): void {
      const cgSelectInstance = this.getCgSelectInstance()
      return cgSelectInstance.resetValidation()
    },
  },
});
</script>
